import { apiSlice } from './apiSlice';
import {
  CatalogueResponse,
  MatchDetailsApiResponse,
  MatchOddsResponse,
  PopularEventsResponse,
  RaceCatalogueResponse,
} from '@/types/MatchData';

export interface Competition {
  id: number;
  externalId: string;
  name: string;
  sport: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitionsResponse {
  success: boolean;
  message: string;
  competitions: Competition[];
}

export interface MatchEvent {
  matchId: string;
  sportId: string;
  seriesId: string;
  name: string;
  matchDateTime: string;
  isSubscribed: boolean;
  is_ended: boolean;
  competition_name: string;
}

export interface ScorecardApiResponse {
  success: boolean;
  message: string;

  scorecardUrl: string | null;
  liveTvUrl: string | null;
}

// Root API Response
export interface FixtureResponse {
  success: boolean;
  message: string;
  fixture: Fixture[];
}

// /fixture/groupByCompetition — nested sport -> competition -> fixtures
export interface GroupedFixtureResponse {
  success: boolean;
  message: string;
  fixture: Record<string, Record<string, Fixture[]>>;
}

export interface MarketPrice {
  price: number;
  size: number;
}

export interface Fixture {
  id: number;
  competitionId: number;
  providerId: number | null;
  externalId: string;
  name: string;
  sport: string;
  startTime: string;
  status: string;
  isFancy: boolean;
  isBookmaker: boolean;
  isPremiumFancy: boolean;
  isPopular: boolean;
  inplay: boolean;

  competition: {
    id: number;
    externalId: string;
    name: string;
  };
  lay: MarketPrice[];
  back: MarketPrice[];
  runners: {
    handicap: number;
    runnerName: string;
    selectionId: string;
    sortPriority: string;
    backPrice1: number;
    backPrice2: number;
    backPrice3: number;
    layPrice1: number;
    layPrice2: number;
    layPrice3: number;
    backSize1: number;
    backSize2: number;
    backSize3: number;
    laySize1: number;
    laySize2: number;
    laySize3: number;
  }[];
}

// Racing Fixture Response
export interface RaceFixtureResponse {
  success: boolean;
  message: string;
  fixture: RaceFixture[];
}

export interface RaceFixture {
  id: string;
  name: string;
  externalId: string;
  sport: string;
  status: string;
  startTime: string;
  inplay: boolean;
  isFancy: boolean;
  isBookmaker: boolean;
  isPremiumFancy: boolean;
  isPopular: boolean;
  competitionId: number;
  providerId: number | null;

  competition: {
    id: number;
    name: string;
    externalId: string;
    startDate: string | null;
  };

  markets: {
    id: string;
    name: string;
    externalId: string;
    startTime: string;
    runner: {
      handicap: number;
      runnerName: string;
      selectionId: string;
      sortPriority: string;
    }[];
  }[];
}

export const sportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSportCompetitions: builder.query<CompetitionsResponse, string>({
      query: (sportName) => ({
        url: `/competitions?`,
        method: 'GET',
        params: { sport: sportName == 'Football' ? 'Soccer' : sportName },
      }),
      providesTags: ['Sports'],
    }),
    getPopularEvents: builder.query<PopularEventsResponse, void>({
      query: () => ({
        url: 'events/popular',
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    getMatches: builder.query<
      FixtureResponse,
      { sportName?: string; inplay?: boolean; seriesId?: string; search?: string; status?: string }
    >({
      query: ({ inplay, sportName, seriesId, search, status }) => ({
        url: '/fixture',
        params: {
          // Legacy callers (e.g. Sidebar) pass lowercase 'cricket'/'tennis'/'soccer' —
          // keep proper-casing those. Any other sport name (already proper-cased by
          // its caller, e.g. Navbar's '/all-sports/:sportName' routes) passes through.
          sport: ['cricket', 'tennis', 'soccer'].includes(sportName ?? '')
            ? sportName!.charAt(0).toUpperCase() + sportName!.slice(1)
            : sportName,
          competitionId: seriesId,
          search,
          inplay,
          status,
        },
      }),
      providesTags: ['Sports'],
    }),
    getMatchesGroupedByCompetition: builder.query<
      GroupedFixtureResponse,
      { sportName?: string; sortBy?: 'time' | 'competition'; inplay?: boolean; search?: string }
    >({
      query: ({ sportName, sortBy = 'time', inplay, search }) => ({
        url: '/fixture/groupByCompetition',
        params: {
          sport: sportName === 'football' ? 'Soccer' : sportName,
          sortBy,
          ...(inplay === undefined ? {} : { inplay }),
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['Sports'],
    }),
    getRaces: builder.query<
      RaceFixtureResponse,
      { sportName?: string; inplay?: boolean; seriesId?: string; search?: string; status?: string }
    >({
      query: ({ inplay, sportName, seriesId, search, status }) => ({
        url: '/fixture/race',
        params: {
          sport: sportName ?? undefined,
          competitionId: seriesId,
          search,
          inplay,
          status,
        },
      }),
      providesTags: ['Sports'],
    }),
    getMatcheDetails: builder.query<CatalogueResponse, { sportName: string; eventId: string }>({
      query: ({ sportName, eventId }) => {
        let url = `/catalogue/${eventId}`;
        return url;
      },
      providesTags: ['Sports'],
    }),
    getRaceDetails: builder.query<RaceCatalogueResponse, { eventId: string }>({
      query: ({ eventId }) => {
        let url = `/catalogue/${eventId}`;
        return url;
      },
      providesTags: ['Sports'],
    }),
    getMediaUrls: builder.query<ScorecardApiResponse, string>({
      query: (eventId) => ({
        url: `events/scorecard/${eventId}`,
      }),
      providesTags: ['Sports'],
    }),
  }),
});

export const {
  useGetSportCompetitionsQuery,
  useGetMatchesQuery,
  useLazyGetMatchesQuery,
  useGetMatchesGroupedByCompetitionQuery,
  useLazyGetMatchesGroupedByCompetitionQuery,
  useGetMatcheDetailsQuery,
  useGetMediaUrlsQuery,
  useGetRacesQuery,
  useGetRaceDetailsQuery,
  useGetPopularEventsQuery,
  useLazyGetSportCompetitionsQuery,
} = sportsApi;
